const HERO_IMAGE =
  'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1600&q=80';

const DIAGRAM_LEGEND = [
  { color: '#fbbf24', label: 'GK — Goalkeeper' },
  { color: '#3b82f6', label: 'DEF — Defenders' },
  { color: '#22c55e', label: 'MID — Midfielders' },
  { color: '#ef4444', label: 'FWD — Forward' },
];

/** Base 2-3-1 shape: goal on left, attack toward right */
const BASE_PLAYERS = [
  { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
  { label: 'LB', roleType: 'DEF', x: 20, y: 22 },
  { label: 'RB', roleType: 'DEF', x: 20, y: 43 },
  { label: 'LM', roleType: 'MID', x: 38, y: 12 },
  { label: 'CM', roleType: 'MID', x: 38, y: 32.5 },
  { label: 'RM', roleType: 'MID', x: 38, y: 53 },
  { label: 'ST', roleType: 'FWD', x: 62, y: 32.5 },
];

/** Featured guide: 7v7 soccer formation 2-3-1 with position roles and movement diagrams */
export const SEVEN_V_SEVEN_FORMATION_POST = {
  id: 'featured-7v7-formation-2-3-1',
  slug: '7v7-soccer-formation-2-3-1-guide',
  title: '7v7 Soccer Formation Guide: 2-3-1 Explained with Movement Diagrams',
  excerpt:
    'Learn the most popular 7v7 shape—1 goalkeeper, 2 defenders, 3 midfielders, 1 striker—with role breakdowns, attacking runs, defensive shifts, and half-pitch diagrams for coaches and players.',
  publishedAt: '2026-07-27T23:45:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Tactics Desk',
  blogCategory: 'exercise',
  audience: '7v7 teams',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Formation overview',
      items: [
        { value: '2-3-1', label: 'Formation shape' },
        { value: '7', label: 'Players on field' },
        { value: '6 roles', label: 'GK · DEF · MID · ST' },
        { value: '5 diagrams', label: 'Shape & movement' },
      ],
    },
    {
      type: 'panel',
      title: 'Why 2-3-1 for 7v7?',
      paragraphs: [
        'In 7v7 soccer (six outfield players plus a goalkeeper), the **2-3-1** is the most balanced shape for ages 8–12. You get a clear defensive line of two, a midfield triangle that can control the center and stretch wide, and a lone striker to pin the back line.',
        'This guide uses half-pitch diagrams with **goal on the left** and **attack toward the right**. Solid arrows show passes; dashed orange arrows show off-ball runs. Teach one role at a time, then layer movement in attack and defense.',
      ],
      chips: ['7v7', '2-3-1', 'Youth soccer', 'Tactical basics'],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 1 — Default 2-3-1 shape (at rest)',
      caption:
        'Starting positions when the ball is in the middle third. Defenders stay goal-side of the ball; midfielders form a flat three; the striker stays high enough to stretch the defense.',
      legend: DIAGRAM_LEGEND,
      players: BASE_PLAYERS,
      arrows: [],
    },
    {
      type: 'table',
      title: 'Each position explained',
      intro: 'Seven players, six distinct jobs. In rec leagues, rotate players through roles—but always name the job so everyone knows what to do.',
      headers: ['Pos.', 'Role', 'Primary job', 'Key habits'],
      rows: [
        [
          'GK',
          'Goalkeeper',
          'Protect the goal; start build-up with safe distribution.',
          'Set position before every shot; communicate “keeper!” and “away!”; roll or throw to feet when under pressure.',
        ],
        [
          'LB / RB',
          'Defenders (center backs in 7v7)',
          'Mark attackers, block shots, win tackles, play simple passes forward.',
          'Stay goal-side and ball-side; don’t chase into midfield; pass to CM or wide mid, not boot long unless trapped.',
        ],
        [
          'LM / RM',
          'Wide midfielders',
          'Provide width in attack; tuck in to help defend the flanks when the ball is on the other side.',
          'Receive on the touchline; look inside first; sprint back when possession is lost.',
        ],
        [
          'CM',
          'Center midfielder',
          'Link defense and attack; receive between the lines; switch play.',
          'Scan before receiving; play one-touch when possible; be the loudest voice organizing shape.',
        ],
        [
          'ST',
          'Striker',
          'Hold up play, make runs behind, finish in the box.',
          'Stay between defenders when possible; time runs diagonally, not straight at offside; press the ball from the front when defending.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Goalkeeper (GK)',
      paragraphs: [
        'The goalkeeper is the last defender and the first attacker. In 7v7, keepers touch the ball often—so foot skills matter as much as shot stopping.',
      ],
      list: [
        'Positioning: center of goal; one step forward when the ball is wide; closer to near post when the ball is on that side.',
        'Distribution: short rolls to a defender’s feet to start build-up; throws to wide mids when the center is blocked.',
        'Communication: call “keeper” for aerial balls; direct defenders to “step” or “drop.”',
        'Movement: rarely leave the box in 7v7 unless on a corner and your league allows it.',
      ],
    },
    {
      type: 'panel',
      title: 'Defenders (LB & RB)',
      paragraphs: [
        'In 2-3-1 the two “backs” are really center backs split left and right. They should work as a pair—when one steps to the ball, the other tucks central.',
      ],
      list: [
        'Defending: delay the attacker; force play wide or backward; tackle only when sure.',
        'Spacing: stay within 8–12 yards of each other; never both chase the same player.',
        'Attack: push up to the midfield line when your team has the ball wide; overlap only if the CM covers centrally.',
        'Movement: slide as a unit when the ball switches sides—see Diagram 4.',
      ],
    },
    {
      type: 'panel',
      title: 'Midfielders (LM, CM, RM)',
      paragraphs: [
        'The midfield three wins 7v7 games. The CM controls tempo; wide mids create width and help fullbacks on defense.',
      ],
      list: [
        'LM / RM: hug the touchline in possession; cut inside only after a pass to create space; track back to the edge of your own box when defending.',
        'CM: always offer a passing angle to the ball carrier; play forward when the striker’s run is on; drop between the two defenders when building from the back.',
        'Triangle play: LM–CM–RM should form a triangle—short passes beat a single presser in small-sided games.',
      ],
    },
    {
      type: 'panel',
      title: 'Striker (ST)',
      paragraphs: [
        'One forward means every run counts. The striker stretches the defense vertically and horizontally so midfielders have space to dribble or pass.',
      ],
      list: [
        'In possession: check to the ball to link play, then spin behind on a one-two; make diagonal runs into the channels, not straight at the keeper.',
        'Out of possession: press the nearest center back or midfielder with the ball; cut passing lanes to the CM.',
        'Movement: stay high and wide enough that one defender cannot mark you and cover the center at the same time.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 2 — Attacking shape (in possession)',
      caption:
        'When your team has the ball in the attacking third: defenders push higher, wide mids stretch the field, CM supports under the ball, striker pins the last defender.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 28, y: 18 },
        { label: 'RB', roleType: 'DEF', x: 28, y: 47 },
        { label: 'LM', roleType: 'MID', x: 55, y: 8 },
        { label: 'CM', roleType: 'MID', x: 50, y: 32.5 },
        { label: 'RM', roleType: 'MID', x: 55, y: 57 },
        { label: 'ST', roleType: 'FWD', x: 76, y: 32.5 },
      ],
      arrows: [
        { from: { x: 55, y: 8 }, to: { x: 76, y: 28 }, style: 'run', label: 'Cross' },
        { from: { x: 50, y: 32.5 }, to: { x: 76, y: 32.5 }, style: 'pass', label: 'Through' },
        { from: { x: 55, y: 57 }, to: { x: 76, y: 38 }, style: 'run', label: 'Far post' },
      ],
    },
    {
      type: 'panel',
      title: 'Movement in attack — what to coach',
      orderedList: [
        'Wide mids (LM/RM) push high and wide—stretch the opponent’s back line.',
        'CM arrives late into the top of the box for rebounds and cut-backs.',
        'Striker makes opposite runs: if LM crosses, ST attacks far post; if RM cuts in, ST drags to near post.',
        'Defenders hold the line at the top of the defensive third—ready to recover if the ball is lost.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 3 — Defensive shape (out of possession)',
      caption:
        'Drop into a compact block: midfielders slide toward the ball, defenders protect the center, striker presses the ball carrier while cutting the pass to CM.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 18, y: 24 },
        { label: 'RB', roleType: 'DEF', x: 18, y: 41 },
        { label: 'LM', roleType: 'MID', x: 32, y: 18 },
        { label: 'CM', roleType: 'MID', x: 30, y: 32.5 },
        { label: 'RM', roleType: 'MID', x: 32, y: 47 },
        { label: 'ST', roleType: 'FWD', x: 44, y: 32.5 },
      ],
      arrows: [
        { from: { x: 44, y: 32.5 }, to: { x: 52, y: 32.5 }, style: 'run', label: 'Press' },
        { from: { x: 32, y: 18 }, to: { x: 36, y: 28 }, style: 'run', label: 'Slide' },
        { from: { x: 32, y: 47 }, to: { x: 36, y: 37 }, style: 'run', label: 'Slide' },
      ],
    },
    {
      type: 'panel',
      title: 'Movement in defense — what to coach',
      list: [
        'Striker: angle the press to show the ball wide, not through the middle.',
        'CM: screen passes into the center; don’t dive in unless support is behind.',
        'Wide mids: when the ball is on your side, tuck in and help the defender; when it’s far, pinch central.',
        'Defenders: one steps, one covers; never both jump to the same attacker.',
        'Goalkeeper: organize the line—“step up,” “hold,” “keeper on.”',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 4 — Build-up from the back',
      caption:
        'Typical passing sequence: goalkeeper to a defender, into the center mid, then switch to the far wide mid to change the point of attack.',
      legend: DIAGRAM_LEGEND,
      players: BASE_PLAYERS,
      arrows: [
        { from: { x: 8, y: 32.5 }, to: { x: 20, y: 22 }, style: 'pass', label: '1' },
        { from: { x: 20, y: 22 }, to: { x: 38, y: 32.5 }, style: 'pass', label: '2' },
        { from: { x: 38, y: 32.5 }, to: { x: 38, y: 53 }, style: 'pass', label: '3' },
        { from: { x: 38, y: 53 }, to: { x: 62, y: 32.5 }, style: 'pass', label: '4' },
      ],
    },
    {
      type: 'panel',
      title: 'Build-up coaching points',
      paragraphs: [
        'Young teams default to kicking long. Train the four-pass pattern in Diagram 4 until it’s automatic—then add the striker’s checking run on pass 3 so pass 4 can be forward instead of sideways.',
      ],
      list: [
        'GK: play to the defender’s open foot, not into pressure.',
        'Defender: first touch forward or wide; if pressed, lay back to GK or play to CM.',
        'CM: open body shape to see both wide mids before receiving.',
        'Switch: when the far-side wide mid is open, change the field in one or two touches.',
      ],
    },
    {
      type: 'formationDiagram',
      title: 'Diagram 5 — Counter-attack movement',
      caption:
        'After winning the ball in midfield: CM plays early into space for the striker; wide mids sprint into the channels for support or a cut-back.',
      legend: DIAGRAM_LEGEND,
      players: [
        { label: 'GK', roleType: 'GK', x: 8, y: 32.5 },
        { label: 'LB', roleType: 'DEF', x: 22, y: 22 },
        { label: 'RB', roleType: 'DEF', x: 22, y: 43 },
        { label: 'LM', roleType: 'MID', x: 48, y: 10 },
        { label: 'CM', roleType: 'MID', x: 42, y: 32.5 },
        { label: 'RM', roleType: 'MID', x: 52, y: 55 },
        { label: 'ST', roleType: 'FWD', x: 72, y: 28 },
      ],
      arrows: [
        { from: { x: 42, y: 32.5 }, to: { x: 72, y: 28 }, style: 'pass', label: 'Early ball' },
        { from: { x: 48, y: 10 }, to: { x: 78, y: 18 }, style: 'run', label: 'Support' },
        { from: { x: 52, y: 55 }, to: { x: 80, y: 48 }, style: 'run', label: 'Trail' },
      ],
    },
    {
      type: 'table',
      title: 'When to use 2-3-1 vs other 7v7 shapes',
      headers: ['Formation', 'Best when…', 'Trade-off'],
      rows: [
        [
          '2-3-1 (this guide)',
          'Balanced team; you want width and a clear striker',
          'CM must work hard both ways',
        ],
        [
          '2-1-2-1 (diamond)',
          'Strong center mid; narrow field',
          'Less natural width; wing play suffers',
        ],
        [
          '3-2-1',
          'Opponent plays one up top; you need extra cover',
          'Fewer passing options in midfield',
        ],
        [
          '2-2-2',
          'Equal athletes; heavy pressing style',
          'No dedicated striker—forwards must coordinate runs',
        ],
      ],
    },
    {
      type: 'table',
      title: 'Quick reference — 2-3-1 responsibilities',
      headers: ['Phase', 'GK', 'DEF', 'MID', 'ST'],
      rows: [
        [
          'We have the ball',
          'Offer angle; distribute short',
          'Push wide and high; overlap cautiously',
          'Create width; CM links',
          'Pin defenders; time runs',
        ],
        [
          'They have the ball',
          'Organize line; claim crosses',
          'Protect center; delay',
          'Slide to ball side; tuck in',
          'Press ball carrier',
        ],
        [
          'Transition (win it)',
          'Stay home unless 1v1',
          'Hold depth first 2 seconds',
          'Play forward quickly',
          'Sprint behind',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Practice plan (60 minutes)',
      orderedList: [
        '10 min — Rondo 4v2 in a grid; CM must touch every successful sequence.',
        '15 min — Build-up pattern from Diagram 4 unopposed, then add one passive defender.',
        '15 min — 7v7 scrimmage: goal only counts if wide mid touched the ball in the attack.',
        '10 min — Defensive slide drill: coach moves ball side to side; midfielders slide as in Diagram 3.',
        '10 min — Free scrimmage; rotate positions every 5 minutes.',
      ],
      callout:
        'Use the Soccer app to split balanced 7v7 sides and share the formation link with parents before practice.',
    },
  ],
  relatedPostsTitle: 'More position training guides',
  relatedPosts: [
    {
      slug: '7v7-defender-positioning-pressing-guide',
      title: '7v7 Defenders: Positioning & Pressing When the Other Team Attacks',
      blurb: 'compact shape, press triggers, and animated defending diagrams.',
    },
    {
      slug: '7v7-soccer-formation-over-35-guide',
      title: '7v7 Soccer Formation for Ages 35+: 2-2-2 Shape & Diagrams',
      blurb: 'adult rec tactics with energy-smart positioning.',
    },
    {
      slug: 'soccer-goalkeeper-drills-free-youtube-videos',
      title: 'Soccer Goalkeeper Drills + Free YouTube Training Videos',
      blurb: 'footwork, handling, and distribution for keepers.',
    },
    {
      slug: 'soccer-defender-drills-free-youtube-videos',
      title: 'Soccer Defender Drills + Free YouTube Training Videos',
      blurb: '1v1 defending and positioning.',
    },
    {
      slug: 'soccer-forward-striker-drills-free-youtube-videos',
      title: 'Soccer Forward & Striker Drills + Free YouTube Videos',
      blurb: 'finishing and movement for your ST.',
    },
    {
      slug: 'open-soccer-fields-powell-dublin-lewis-center-delaware-ohio',
      title: 'Open Soccer Fields Near Powell, Dublin, Lewis Center & Delaware',
      blurb: 'find a 7v7-sized pitch for your next session.',
    },
  ],
  referenceLinks: [
    {
      label: 'US Youth Soccer — small-sided games resources',
      url: 'https://www.usyouthsoccer.org/',
    },
    {
      label: 'U.S. Soccer — player development initiatives',
      url: 'https://www.ussoccer.com/players/player-development/initiatives',
    },
  ],
};
